'use client';

import React from 'react';
import {AlertTriangle, CheckCircle, Info, XCircle} from 'lucide-react';

import {Button} from '@/components/ui/button';
import {cn} from '@/libs/utils';
import type {AlertModalProps} from '@/presentation/stores/ModalStore';

interface AlertModalComponentProps extends AlertModalProps {
    onClose?: () => void;
    modalId?: string;
}

const typeConfig = {
    info: {
        icon: Info,
        iconColor: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
    },
    success: {
        icon: CheckCircle,
        iconColor: 'text-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-yellow-500',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
    },
    error: {
        icon: XCircle,
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
    },
};

export const AlertModal: React.FC<AlertModalComponentProps> = ({
                                                                   title,
                                                                   message,
                                                                   type = 'info',
                                                                   buttonText = 'OK',
                                                                   onClose,
                                                               }) => {
    const config = typeConfig[type];
    const IconComponent = config.icon;

    const handleClose = () => {
        onClose?.();
    };

    return (
        <div className="p-6">
            {/* Header with Icon */}
            <div className={cn(
                'flex items-center gap-3 p-3 rounded-lg mb-4',
                config.bgColor,
                config.borderColor,
                'border'
            )}>
                <IconComponent className={cn('h-6 w-6', config.iconColor)}/>
                <h2 className="text-lg font-semibold text-gray-900">
                    {title}
                </h2>
            </div>

            {/* Content */}
            <div className="mb-6">
                {typeof message === 'string' ? (
                    <p className="text-gray-700 leading-relaxed">
                        {message}
                    </p>
                ) : (
                    message
                )}
            </div>

            {/* Action */}
            <div className="flex justify-end">
                <Button
                    onClick={handleClose}
                    className="px-6 py-2"
                    variant="default"
                >
                    {buttonText}
                </Button>
            </div>
        </div>
    );
};

AlertModal.displayName = 'AlertModal';

export default AlertModal;